import React from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import LoadingState from "../components/common/LoadingState";
import { apiFetch } from "../api/apiFetch";

const endpoints = {
  overview: "/analytics/overview/",
  funnel: "/analytics/funnel/",
  sprints: "/analytics/sprints/",
  drift: "/analytics/drift/",
  social: "/analytics/social/",
  notifications: "/analytics/notifications/",
};

const tabs = [
  { key: "command", label: "Command Center" },
  { key: "overview", label: "Overview", endpointKey: "overview" },
  { key: "funnel", label: "Funnel", endpointKey: "funnel" },
  { key: "sprints", label: "Challenges", endpointKey: "sprints" },
  { key: "drift", label: "Drift", endpointKey: "drift" },
  { key: "social", label: "Social", endpointKey: "social" },
  { key: "notifications", label: "Nudges", endpointKey: "notifications" },
  { key: "raw", label: "Raw API" },
];

const colors = {
  blue: "#1769e0",
  red: "#dc2626",
  yellow: "#d97706",
  green: "#059669",
  ink: "#0f172a",
  muted: "#64748b",
  border: "#dbe7f3",
  card: "#ffffff",
  softBlue: "#eff6ff",
  softRed: "#fef2f2",
  softYellow: "#fffbeb",
  softGreen: "#ecfdf5",
};

function number(value, suffix = "") {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "number") return `${value.toLocaleString()}${suffix}`;
  return String(value);
}

function percent(value) {
  return number(Number(value || 0), "%");
}

function formatDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function userLabel(user) {
  return user?.username ? `@${user.username}` : `User ${user?.id || ""}`;
}

function cardTone(tone) {
  if (tone === "critical") return { border: colors.red, bg: colors.softRed, fg: colors.red };
  if (tone === "warning") return { border: colors.yellow, bg: colors.softYellow, fg: colors.yellow };
  if (tone === "positive") return { border: colors.green, bg: colors.softGreen, fg: colors.green };
  return { border: colors.border, bg: colors.card, fg: colors.blue };
}

function normalizeFunnel(steps = []) {
  return steps.map((step, index) => {
    const previous = index > 0 ? steps[index - 1] : null;
    const previousCount = Number(previous?.count || 0);
    const count = Number(step.count || 0);
    const dropCount = Math.max(previousCount - count, 0);
    const dropRate = previousCount ? Math.round((dropCount / previousCount) * 1000) / 10 : 0;
    return {
      ...step,
      shortLabel: String(step.label || step.key || "").replace("Completed First Reflection", "First Reflection"),
      dropCount,
      dropRate,
      conversion: Number(step.conversion_from_previous ?? 100),
    };
  });
}

function getLargestDrop(funnelRows) {
  return [...funnelRows].slice(1).sort((a, b) => b.dropRate - a.dropRate)[0] || null;
}

function getLowestConversion(funnelRows) {
  return [...funnelRows].slice(1).sort((a, b) => a.conversion - b.conversion)[0] || null;
}

function getWeakestRetention(dayRows = []) {
  return [...dayRows].sort((a, b) => Number(a.retention_rate || 0) - Number(b.retention_rate || 0))[0] || null;
}

function buildInsights({ funnelRows, sprintRows, driftMetrics }) {
  const insights = [];
  const largestDrop = getLargestDrop(funnelRows);
  const lowestConversion = getLowestConversion(funnelRows);
  const weakestRetention = getWeakestRetention(sprintRows);
  const recoveryRate = Number(driftMetrics?.recovery_rate_after_missed_days || 0);

  if (largestDrop?.dropRate > 0) {
    insights.push({
      tone: largestDrop.dropRate >= 50 ? "critical" : "warning",
      title: "Biggest Drop-Off",
      metric: `${largestDrop.dropRate}% lost`,
      problem: `${funnelRows[funnelRows.indexOf(largestDrop) - 1]?.shortLabel || "Previous step"} → ${largestDrop.shortLabel}`,
      interpretation: "Users are not moving cleanly into the next accountability behavior.",
      action: largestDrop.key === "completed_first_reflection"
        ? "Trigger reflection immediately after sprint start and reinforce the daily loop."
        : "Tighten this step with clearer prompts and fewer choices.",
    });
  }

  if (lowestConversion && lowestConversion !== largestDrop) {
    insights.push({
      tone: lowestConversion.conversion < 35 ? "critical" : "warning",
      title: "Lowest Conversion",
      metric: percent(lowestConversion.conversion),
      problem: `${lowestConversion.shortLabel} conversion`,
      interpretation: "This step is the weakest handoff in the product journey.",
      action: "Review the screen before this step and make the next habit unmistakable.",
    });
  }

  if (weakestRetention) {
    insights.push({
      tone: Number(weakestRetention.retention_rate || 0) < 40 ? "critical" : "warning",
      title: "Weakest Challenge Day",
      metric: percent(weakestRetention.retention_rate),
      problem: `Day ${weakestRetention.day} retention`,
      interpretation: "The Challenge habit loop is breaking at this point in the window.",
      action: "Add a targeted nudge and first-habit reminder before this day.",
    });
  }

  insights.push({
    tone: recoveryRate >= 50 ? "positive" : recoveryRate >= 25 ? "warning" : "critical",
    title: "Recovery Performance",
    metric: percent(recoveryRate),
    problem: "Recovery after missed days",
    interpretation:
      recoveryRate >= 50
        ? "Users who drift are finding their way back."
        : "Most users who fall off are not recovering.",
    action:
      recoveryRate >= 50
        ? "Keep recovery nudges visible and measure which ones work."
        : "Build a stronger missed-day recovery loop with a low-friction restart habit.",
  });

  return insights.slice(0, 4);
}

function PageHeader() {
  return (
    <Card
      elevation={0}
      sx={{
        mb: 2.5,
        border: `1px solid ${colors.border}`,
        borderRadius: 5,
        overflow: "hidden",
        background:
          "radial-gradient(circle at 12% 18%, rgba(23,105,224,0.16), transparent 34%), linear-gradient(135deg, #f8fbff 0%, #ffffff 48%, #eef6ff 100%)",
        boxShadow: "0 24px 70px rgba(15, 23, 42, 0.08)",
      }}
    >
      <CardContent sx={{ p: { xs: 2.5, md: 4 } }}>
        <Stack spacing={1.4}>
          <Chip
            label="Internal command center"
            size="small"
            sx={{ width: "fit-content", bgcolor: colors.softBlue, color: "#075985", fontWeight: 900 }}
          />
          <Typography variant="h3" sx={{ fontWeight: 950, color: colors.ink, letterSpacing: "-0.055em" }}>
            TrustBud Analytics
          </Typography>
          <Typography variant="body1" sx={{ color: colors.muted, maxWidth: 760, fontWeight: 600 }}>
            See where users fail, why they fail, and which specific accounts need a closer look.
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  );
}

function AnalyticsTabs({ value, onChange }) {
  return (
    <Card
      elevation={0}
      sx={{
        mb: 3,
        border: `1px solid ${colors.border}`,
        borderRadius: 4,
        overflow: "hidden",
        bgcolor: "rgba(255,255,255,0.92)",
        boxShadow: "0 16px 40px rgba(15, 23, 42, 0.06)",
      }}
    >
      <Tabs
        value={value}
        onChange={(_, nextValue) => onChange(nextValue)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          minHeight: 50,
          px: 1,
          "& .MuiTabs-indicator": { height: 3, bgcolor: colors.blue, borderRadius: 999 },
          "& .MuiTab-root": {
            minHeight: 50,
            color: colors.muted,
            fontWeight: 850,
            letterSpacing: "-0.01em",
            textTransform: "none",
          },
          "& .Mui-selected": { color: `${colors.blue} !important` },
        }}
      >
        {tabs.map((tab) => (
          <Tab key={tab.key} value={tab.key} label={tab.label} />
        ))}
      </Tabs>
    </Card>
  );
}

function EndpointNote({ endpointKey }) {
  if (!endpointKey) return null;
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
      <Typography variant="caption" sx={{ color: colors.muted, fontWeight: 900, textTransform: "uppercase" }}>
        Source
      </Typography>
      <Chip
        label={endpoints[endpointKey]}
        size="small"
        sx={{
          bgcolor: colors.softBlue,
          color: "#075985",
          fontFamily: "monospace",
          fontWeight: 800,
        }}
      />
    </Stack>
  );
}

function Section({ title, question, children }) {
  return (
    <Box sx={{ mt: 3.5 }}>
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1} sx={{ mb: 1.5 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 950, color: colors.ink, letterSpacing: "-0.035em" }}>
            {title}
          </Typography>
          <Typography variant="body2" sx={{ color: colors.muted, mt: 0.3, fontWeight: 600 }}>
            {question}
          </Typography>
        </Box>
      </Stack>
      {children}
    </Box>
  );
}

function OverviewTab({ overview, retention, sprintMetrics, socialMetrics, users }) {
  const retentionMetrics = retention || {};
  return (
    <>
      <EndpointNote endpointKey="overview" />
      <KpiRow overview={overview} sprintMetrics={sprintMetrics} socialMetrics={socialMetrics} />
      <Section title="Retention Snapshot" question="Do new users come back after signup?">
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <KpiCard label="Day 1 Retention" value={percent(retentionMetrics.day_1?.rate)} subtext={`${number(retentionMetrics.day_1?.retained_users)} of ${number(retentionMetrics.day_1?.eligible_users)} eligible users`} tone={retentionMetrics.day_1?.rate < 30 ? "critical" : "positive"} />
          </Grid>
          <Grid item xs={12} md={6}>
            <KpiCard label="Day 7 Retention" value={percent(retentionMetrics.day_7?.rate)} subtext={`${number(retentionMetrics.day_7?.retained_users)} of ${number(retentionMetrics.day_7?.eligible_users)} eligible users`} tone={retentionMetrics.day_7?.rate < 20 ? "critical" : "positive"} />
          </Grid>
        </Grid>
      </Section>
      <UserDrilldown
        groups={[
          { title: "Active in last 7 days", users: users?.active_7d },
          { title: "New in last 7 days", users: users?.new_7d },
          { title: "Inactive in last 7 days", users: users?.inactive_7d },
        ]}
      />
    </>
  );
}

function SprintMetricsTab({ overview, sprintMetrics, socialMetrics, sprints, drift }) {
  return (
    <>
      <EndpointNote endpointKey="sprints" />
      <KpiRow overview={overview} sprintMetrics={sprintMetrics} socialMetrics={socialMetrics} />
      <ConsistencyCharts sprints={sprints} drift={drift} />
      <Section title="Challenge Drilldown" question="Which Challenge relationships need attention?">
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" sx={{ mb: 1, color: colors.ink, fontWeight: 950 }}>
              Active Challenges
            </Typography>
            <SprintDrilldown sprints={sprints?.users?.active_sprints || []} />
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" sx={{ mb: 1, color: colors.ink, fontWeight: 950 }}>
              Needs reflection today
            </Typography>
            <SprintDrilldown sprints={sprints?.users?.needs_reflection || []} />
          </Grid>
        </Grid>
      </Section>
    </>
  );
}

function RawApiExplorer({ data }) {
  return (
    <Section title="Raw API" question="Inspect every analytics endpoint without typing URLs.">
      <Grid container spacing={2}>
        {Object.entries(endpoints).map(([key, endpoint]) => (
          <Grid item xs={12} md={6} key={key}>
            <Card elevation={0} sx={{ border: `1px solid ${colors.border}`, borderRadius: 4, height: "100%" }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                  <Typography variant="subtitle1" sx={{ color: colors.ink, fontWeight: 900, textTransform: "capitalize" }}>
                    {key}
                  </Typography>
                  <Chip
                    label={endpoint}
                    size="small"
                    sx={{ bgcolor: colors.softBlue, color: "#075985", fontFamily: "monospace", fontWeight: 800 }}
                  />
                </Stack>
                <Divider sx={{ my: 1.5 }} />
                <Box
                  component="pre"
                  sx={{
                    m: 0,
                    p: 1.5,
                    maxHeight: 320,
                    overflow: "auto",
                    borderRadius: 2,
                    bgcolor: "#0f172a",
                    color: "#dbeafe",
                    fontSize: 12,
                    lineHeight: 1.55,
                  }}
                >
                  {JSON.stringify(data?.[key] || {}, null, 2)}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Section>
  );
}

function InitialsBadge({ user }) {
  const initials = String(user?.username || user?.email || "?").slice(0, 2).toUpperCase();
  return (
    <Box
      sx={{
        width: 34,
        height: 34,
        borderRadius: 2,
        display: "grid",
        placeItems: "center",
        bgcolor: colors.softBlue,
        color: colors.blue,
        fontSize: 12,
        fontWeight: 950,
        flex: "0 0 auto",
      }}
    >
      {initials}
    </Box>
  );
}

function UserRows({ title, users = [], empty = "No users in this segment." }) {
  return (
    <Card elevation={0} sx={{ border: `1px solid ${colors.border}`, borderRadius: 4, height: "100%" }}>
      <CardContent sx={{ p: 2.5 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
          <Typography variant="subtitle1" sx={{ color: colors.ink, fontWeight: 950 }}>
            {title}
          </Typography>
          <Chip label={users.length} size="small" sx={{ bgcolor: "#f1f5f9", color: colors.muted, fontWeight: 900 }} />
        </Stack>
        <Stack spacing={1}>
          {users.length ? users.slice(0, 10).map((user) => (
            <Box
              key={`${title}-${user.id}`}
              sx={{
                p: 1.2,
                border: "1px solid #eef2f7",
                borderRadius: 2.5,
                bgcolor: "#fbfdff",
              }}
            >
              <Stack direction="row" spacing={1.2} alignItems="center">
                <InitialsBadge user={user} />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={{ color: colors.ink, fontWeight: 900, overflow: "hidden", textOverflow: "ellipsis" }}>
                    {userLabel(user)}
                  </Typography>
                  <Typography variant="caption" sx={{ color: colors.muted }}>
                    {user.email || "No email"} · joined {formatDate(user.date_joined)}
                  </Typography>
                  <Stack direction="row" spacing={0.7} flexWrap="wrap" useFlexGap sx={{ mt: 0.7 }}>
                    {user.timezone ? (
                      <Chip label={user.timezone} size="small" sx={{ height: 22, bgcolor: "#f1f5f9", color: colors.muted, fontWeight: 800 }} />
                    ) : null}
                    {user.active_vision_title ? (
                      <Chip label={user.active_vision_title} size="small" sx={{ height: 22, bgcolor: colors.softBlue, color: colors.blue, fontWeight: 800 }} />
                    ) : null}
                    <Chip label={`${number(user.active_goal_count || 0)} goals`} size="small" sx={{ height: 22, bgcolor: colors.softGreen, color: colors.green, fontWeight: 800 }} />
                    <Chip label={`${number(user.active_commitment_count || 0)} habits`} size="small" sx={{ height: 22, bgcolor: colors.softYellow, color: colors.yellow, fontWeight: 800 }} />
                    {user.discovery_visibility ? (
                      <Chip label={`Discovery: ${user.discovery_visibility}`} size="small" sx={{ height: 22, bgcolor: "#f8fafc", color: colors.muted, fontWeight: 800 }} />
                    ) : null}
                  </Stack>
                  {user.profile_headline || user.profile_focus ? (
                    <Typography variant="caption" sx={{ display: "block", color: colors.muted, mt: 0.6, fontWeight: 700 }}>
                      {[user.profile_headline, user.profile_focus].filter(Boolean).join(" · ")}
                    </Typography>
                  ) : null}
                  {user.last_active_date || user.dropoff_days_ago || user.longest_streak ? (
                    <Typography variant="caption" sx={{ display: "block", color: colors.red, fontWeight: 800 }}>
                      {user.dropoff_days_ago ? `Dropped ${user.dropoff_days_ago}d ago` : ""}
                      {user.last_active_date ? ` · last active ${formatDate(user.last_active_date)}` : ""}
                      {user.longest_streak ? ` · ${user.longest_streak}d best streak` : ""}
                    </Typography>
                  ) : null}
                </Box>
              </Stack>
            </Box>
          )) : (
            <Typography variant="body2" sx={{ color: colors.muted, fontWeight: 600 }}>
              {empty}
            </Typography>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}

function UserDrilldown({ title = "User Drilldown", groups = [] }) {
  const visibleGroups = groups.filter(Boolean);
  if (!visibleGroups.length) return null;
  return (
    <Section title={title} question="Which specific users are included in these numbers?">
      <Grid container spacing={2}>
        {visibleGroups.map((group) => (
          <Grid item xs={12} md={group.wide ? 12 : 4} key={group.title}>
            <UserRows title={group.title} users={group.users || []} empty={group.empty} />
          </Grid>
        ))}
      </Grid>
    </Section>
  );
}

function FunnelDropoffDrilldown({ dropoffs = [] }) {
  if (!dropoffs.length) return null;
  const readableStep = (value) => String(value || "").replace(/_/g, " ");
  return (
    <Section title="Drop-Off Drilldown" question="Who reached one step but did not reach the next?">
      <Grid container spacing={2}>
        {dropoffs.map((dropoff) => (
          <Grid item xs={12} md={6} key={`${dropoff.from}-${dropoff.to}`}>
            <UserRows
              title={`${readableStep(dropoff.from)} → ${readableStep(dropoff.to)}`}
              users={dropoff.users || []}
              empty="No drop-off users for this step."
            />
          </Grid>
        ))}
      </Grid>
    </Section>
  );
}

function SprintDrilldown({ sprints = [] }) {
  if (!sprints.length) return null;
  return (
    <Card elevation={0} sx={{ border: `1px solid ${colors.border}`, borderRadius: 4 }}>
      <CardContent sx={{ p: 2.5 }}>
        <Stack spacing={1.2}>
          {sprints.slice(0, 12).map((sprint) => (
            <Box key={sprint.id} sx={{ p: 1.5, borderRadius: 2.5, bgcolor: "#fbfdff", border: "1px solid #eef2f7" }}>
              <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1.5}>
                <Box>
                  <Typography sx={{ color: colors.ink, fontWeight: 950 }}>
                    Sprint #{sprint.id} · {sprint.status}
                  </Typography>
                  <Typography variant="body2" sx={{ color: colors.muted }}>
                    {userLabel(sprint.user_a)} with {userLabel(sprint.user_b)}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip label={sprint.recovery_state || "normal"} size="small" sx={{ bgcolor: colors.softYellow, color: colors.yellow, fontWeight: 800 }} />
                  <Chip label={`${formatDate(sprint.start_date)} → ${formatDate(sprint.end_date)}`} size="small" sx={{ bgcolor: "#f1f5f9", color: colors.muted, fontWeight: 800 }} />
                </Stack>
              </Stack>
            </Box>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
}

function ConnectionDrilldown({ connections = [] }) {
  if (!connections.length) return null;
  return (
    <Section title="Connection Drilldown" question="Which relationships are driving the social metrics?">
      <Card elevation={0} sx={{ border: `1px solid ${colors.border}`, borderRadius: 4 }}>
        <CardContent sx={{ p: 2.5 }}>
          <Stack spacing={1.2}>
            {connections.slice(0, 12).map((connection) => (
              <Box key={connection.id} sx={{ p: 1.5, borderRadius: 2.5, bgcolor: "#fbfdff", border: "1px solid #eef2f7" }}>
                <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1.5}>
                  <Typography sx={{ color: colors.ink, fontWeight: 900 }}>
                    {userLabel(connection.user_a)} ↔ {userLabel(connection.user_b)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: colors.muted, fontWeight: 700 }}>
                    Last interaction {formatDate(connection.last_interaction_at)}
                  </Typography>
                </Stack>
              </Box>
            ))}
          </Stack>
        </CardContent>
      </Card>
    </Section>
  );
}

function NudgeDrilldown({ nudges = [] }) {
  if (!nudges.length) return null;
  return (
    <Section title="Nudge Drilldown" question="Which nudges were sent, and did the user act?">
      <Card elevation={0} sx={{ border: `1px solid ${colors.border}`, borderRadius: 4 }}>
        <CardContent sx={{ p: 2.5 }}>
          <Stack spacing={1.2}>
            {nudges.slice(0, 15).map((nudge) => (
              <Box key={nudge.id} sx={{ p: 1.5, borderRadius: 2.5, bgcolor: "#fbfdff", border: "1px solid #eef2f7" }}>
                <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1.5}>
                  <Box>
                    <Typography sx={{ color: colors.ink, fontWeight: 950 }}>
                      {userLabel(nudge.user)} · {nudge.nudge_key || nudge.period}
                    </Typography>
                    <Typography variant="body2" sx={{ color: colors.muted }}>
                      {nudge.target_action || "No target habit"} · {nudge.channel || "unknown channel"} · {formatDate(nudge.sent_at)}
                    </Typography>
                  </Box>
                  <Chip
                    label={nudge.acted ? "Acted" : "No habit"}
                    size="small"
                    sx={{
                      bgcolor: nudge.acted ? colors.softGreen : colors.softRed,
                      color: nudge.acted ? colors.green : colors.red,
                      fontWeight: 900,
                    }}
                  />
                </Stack>
              </Box>
            ))}
          </Stack>
        </CardContent>
      </Card>
    </Section>
  );
}

function InsightCards({ insights }) {
  return (
    <Section title="Key Insights" question="What needs attention first?">
      <Grid container spacing={2}>
        {insights.map((insight) => {
          const tone = cardTone(insight.tone);
          return (
            <Grid item xs={12} md={6} key={`${insight.title}-${insight.problem}`}>
              <Card
                elevation={0}
                sx={{
                  height: "100%",
                  border: `1px solid ${tone.border}`,
                  bgcolor: tone.bg,
                  borderRadius: 4,
                  boxShadow: "0 18px 45px rgba(15, 23, 42, 0.08)",
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Stack direction="row" justifyContent="space-between" spacing={2}>
                    <Typography variant="overline" sx={{ color: tone.fg, fontWeight: 900 }}>
                      {insight.title}
                    </Typography>
                    <Typography variant="h4" sx={{ color: tone.fg, fontWeight: 950 }}>
                      {insight.metric}
                    </Typography>
                  </Stack>
                  <Typography variant="h6" sx={{ mt: 1, fontWeight: 900, color: colors.ink }}>
                    {insight.problem}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1.2, color: "#334155" }}>
                    {insight.interpretation}
                  </Typography>
                  <Box sx={{ mt: 2, p: 1.4, borderRadius: 2.5, bgcolor: "rgba(255,255,255,0.72)" }}>
                    <Typography variant="caption" sx={{ color: colors.muted, fontWeight: 900, textTransform: "uppercase" }}>
                      Suggested next move
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.4, color: colors.ink, fontWeight: 700 }}>
                      {insight.action}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Section>
  );
}

function KpiCard({ label, value, subtext, tone }) {
  const color = cardTone(tone);
  return (
    <Card
      elevation={0}
      sx={{
        border: `1px solid ${colors.border}`,
        borderRadius: 4,
        height: "100%",
        bgcolor: "#ffffff",
        boxShadow: "0 14px 40px rgba(15, 23, 42, 0.055)",
      }}
    >
      <CardContent sx={{ p: 2.5 }}>
        <Typography variant="caption" sx={{ color: colors.muted, fontWeight: 900, textTransform: "uppercase" }}>
          {label}
        </Typography>
        <Typography variant="h3" sx={{ mt: 0.8, color: color.fg, fontWeight: 950, letterSpacing: "-0.04em" }}>
          {value}
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5, color: colors.muted }}>
          {subtext}
        </Typography>
      </CardContent>
    </Card>
  );
}

function KpiRow({ overview, sprintMetrics, socialMetrics }) {
  return (
    <Section title="KPI Snapshot" question="Is the product creating active accountability?">
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={2.4}>
          <KpiCard label="Total Users" value={number(overview.total_users)} subtext="All accounts" />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <KpiCard label="Active Users" value={number(overview.active_users_7d)} subtext={`${overview.active_rate_7d || 0}% active in 7d`} tone="positive" />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <KpiCard label="Active Challenges" value={number(sprintMetrics.total_active_sprints)} subtext="Live focused Challenges" tone="positive" />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <KpiCard label="Challenge Completion" value={percent(sprintMetrics.avg_sprint_completion_rate)} subtext="Average reflection completion" tone={sprintMetrics.avg_sprint_completion_rate < 40 ? "critical" : "default"} />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <KpiCard label="With TrustBud" value={percent(socialMetrics.users_with_partner_rate)} subtext="Users with partner" tone="default" />
        </Grid>
      </Grid>
    </Section>
  );
}

function FunnelChart({ rows }) {
  const largestDrop = getLargestDrop(rows);
  const max = Math.max(...rows.map((row) => Number(row.count || 0)), 1);
  return (
    <Section title="Funnel" question="Where users drop off.">
      <Card elevation={0} sx={{ border: `1px solid ${colors.border}`, borderRadius: 4, p: 2.5 }}>
        <Stack spacing={2}>
          {rows.map((row, index) => {
            const isLeak = row.key === largestDrop?.key;
            return (
              <Box key={row.key}>
                <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.7 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography sx={{ minWidth: 145, color: colors.ink, fontWeight: 850 }}>
                      {row.shortLabel}
                    </Typography>
                    {isLeak ? <Chip label="largest leak" size="small" sx={{ bgcolor: colors.softRed, color: colors.red, fontWeight: 800 }} /> : null}
                  </Stack>
                  <Typography variant="body2" sx={{ color: isLeak ? colors.red : colors.muted, fontWeight: 800 }}>
                    {number(row.count)} {index > 0 ? `· ${percent(row.conversion)} from previous` : ""}
                  </Typography>
                </Stack>
                <Box sx={{ height: 22, bgcolor: "#eef2f7", borderRadius: 999, overflow: "hidden" }}>
                  <Box
                    sx={{
                      height: "100%",
                      width: `${Math.max((Number(row.count || 0) / max) * 100, row.count ? 4 : 0)}%`,
                      bgcolor: isLeak ? colors.red : colors.blue,
                      borderRadius: 999,
                    }}
                  />
                </Box>
              </Box>
            );
          })}
        </Stack>
      </Card>
    </Section>
  );
}

function PrimaryLeakCard({ rows }) {
  const leak = getLargestDrop(rows);
  const previous = leak ? rows[rows.indexOf(leak) - 1] : null;
  if (!leak) return null;
  return (
    <Section title="Primary Leak Focus" question="The one handoff to fix first.">
      <Card
        elevation={0}
        sx={{
          border: `1px solid ${colors.red}`,
          borderRadius: 4,
          bgcolor: colors.softRed,
          boxShadow: "0 18px 45px rgba(220, 38, 38, 0.1)",
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <Typography variant="overline" sx={{ color: colors.red, fontWeight: 950 }}>
                Primary Leak
              </Typography>
              <Typography variant="h4" sx={{ color: colors.ink, fontWeight: 950, letterSpacing: "-0.04em" }}>
                {previous?.shortLabel} → {leak.shortLabel}
              </Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="h2" sx={{ color: colors.red, fontWeight: 950, letterSpacing: "-0.06em" }}>
                {percent(leak.conversion)}
              </Typography>
              <Typography variant="body2" sx={{ color: colors.muted, fontWeight: 700 }}>
                conversion
              </Typography>
            </Grid>
            <Grid item xs={12} md={5}>
              <Typography variant="body1" sx={{ color: colors.ink, fontWeight: 800 }}>
                Users are not forming the next habit loop after this step.
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, color: colors.muted }}>
                Make the next habit immediate, obvious, and low-friction. If this is Sprint → Reflection, show the reflection nudge inside the sprint start landing state.
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Section>
  );
}

function ConsistencyCharts({ sprints, drift }) {
  const dayRows = (sprints?.day_breakdown || []).map((row) => ({
    day: `Day ${row.day}`,
    retention: Number(row.retention_rate || 0),
    dropoffs: Number(row.dropoffs || 0),
  }));
  const missedRows = (drift?.dropoff_by_recent_day || []).map((row) => ({
    day: `${row.days_ago}d ago`,
    users: Number(row.users || 0),
  }));

  return (
    <Section title="Consistency Engine" question="Where consistency breaks.">
      <Grid container spacing={2}>
        <Grid item xs={12} md={7}>
          <ChartCard title="Drop-Off by Challenge Day">
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={dayRows} margin={{ top: 24, right: 18, left: -12, bottom: 4 }}>
                <CartesianGrid vertical={false} stroke="#e2e8f0" />
                <XAxis dataKey="day" tickLine={false} axisLine={false} />
                <YAxis tickLine={false} axisLine={false} />
                <Tooltip />
                <Bar dataKey="retention" name="Retention %" radius={[8, 8, 0, 0]}>
                  {dayRows.map((row) => (
                    <Cell key={row.day} fill={row.retention < 40 ? colors.red : colors.blue} />
                  ))}
                  <LabelList dataKey="retention" position="top" formatter={(value) => `${value}%`} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={5}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={12}>
              <KpiCard label="Average Streak" value={number(drift?.metrics?.avg_streak_length)} subtext="Longest active run per user" />
            </Grid>
            <Grid item xs={12} sm={6} md={12}>
              <KpiCard label="At-Risk Users" value={percent(drift?.metrics?.missed_2_plus_days_rate)} subtext="Missed 2+ days in a row" tone="critical" />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <ChartCard title="Missed Days Distribution">
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={missedRows} margin={{ top: 24, right: 18, left: -12, bottom: 4 }}>
                <CartesianGrid vertical={false} stroke="#e2e8f0" />
                <XAxis dataKey="day" tickLine={false} axisLine={false} />
                <YAxis tickLine={false} axisLine={false} />
                <Tooltip />
                <Bar dataKey="users" name="Users" fill={colors.yellow} radius={[8, 8, 0, 0]}>
                  <LabelList dataKey="users" position="top" />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
      </Grid>
    </Section>
  );
}

function ChartCard({ title, children }) {
  return (
    <Card
      elevation={0}
      sx={{
        border: `1px solid ${colors.border}`,
        borderRadius: 4,
        height: "100%",
        minHeight: 320,
        bgcolor: "#ffffff",
        boxShadow: "0 16px 45px rgba(15, 23, 42, 0.06)",
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 2, color: colors.ink, fontWeight: 900 }}>
          {title}
        </Typography>
        {children}
      </CardContent>
    </Card>
  );
}

function RecoverySection({ drift }) {
  const metrics = drift?.metrics || {};
  const missedRate = Number(metrics.missed_2_plus_days_rate || 0);
  const recoveryRate = Number(metrics.recovery_rate_after_missed_days || 0);
  const recoveredProxy = Math.round((missedRate * recoveryRate) / 100);
  return (
    <Section title="Recovery Performance" question="Do users come back after they fall off?">
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <KpiCard label="Missed Users" value={percent(missedRate)} subtext="Missed 2+ days proxy" tone="critical" />
        </Grid>
        <Grid item xs={12} md={4}>
          <KpiCard label="Recovered" value={`${recoveredProxy}%`} subtext="Approx. recovered segment" tone={recoveryRate >= 50 ? "positive" : "warning"} />
        </Grid>
        <Grid item xs={12} md={4}>
          <KpiCard label="Recovery Rate" value={percent(recoveryRate)} subtext={recoveryRate < 30 ? "Most users who fall off do not recover." : "Recovery loop is showing traction."} tone={recoveryRate < 30 ? "critical" : "positive"} />
        </Grid>
      </Grid>
    </Section>
  );
}

function AccountabilityComparison({ social }) {
  const rows = (social?.comparison || []).map((row) => ({
    label: row.label,
    completion: Number(row.rate || 0),
    retention: row.label === "With partner" ? Number(social?.metrics?.sprint_success_with_partner || 0) : Number(social?.metrics?.consistency_without_partner || 0),
    streak: row.label === "With partner" ? Number(row.rate || 0) : Number(row.rate || 0),
  }));
  return (
    <Section title="Accountability Impact" question="Does a TrustBud improve consistency?">
      <ChartCard title="With TrustBud vs Without TrustBud">
        <ResponsiveContainer width="100%" height={340}>
          <BarChart data={rows} margin={{ top: 24, right: 18, left: -12, bottom: 4 }}>
            <CartesianGrid vertical={false} stroke="#e2e8f0" />
            <XAxis dataKey="label" tickLine={false} axisLine={false} />
            <YAxis tickLine={false} axisLine={false} />
            <Tooltip />
            <Legend />
            <Bar dataKey="completion" name="Completion / consistency %" fill={colors.blue} radius={[8, 8, 0, 0]}>
              <LabelList dataKey="completion" position="top" formatter={(value) => `${value}%`} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>
    </Section>
  );
}

function NudgeEffectiveness({ notifications }) {
  const metrics = notifications?.metrics || {};
  const funnel = notifications?.funnel_7d || notifications?.funnel || {};
  const rows = (notifications?.by_period || []).slice(0, 8).map((row) => ({
    type: row.period,
    sent: Number(row.count || 0),
  }));
  const funnelRows = [
    { step: "Sent", count: Number(funnel.sent || 0) },
    { step: "Opened", count: Number(funnel.opened || 0) },
    { step: "Clicked", count: Number(funnel.clicked || 0) },
    { step: "Dismissed", count: Number(funnel.dismissed || 0) },
    { step: "Acted", count: Number(funnel.acted || 0) },
  ];
  const familyRows = (notifications?.by_family || []).slice(0, 8).map((row) => ({
    family: row.family || "unknown",
    sent: Number(row.sent || 0),
    acted: Number(row.acted || 0),
    dismissed: Number(row.dismissed || 0),
  }));
  return (
    <Section title="Nudge Effectiveness" question="Are nudges leading to follow-through?">
      <Grid container spacing={2}>
        <Grid item xs={12} md={3}>
          <KpiCard label="Nudges Sent" value={number(metrics.notifications_sent_7d)} subtext="Last 7 days" />
        </Grid>
        <Grid item xs={12} md={3}>
          <KpiCard label="Habits After Nudges" value={number(metrics.actions_after_nudges)} subtext="Attributed acted events" />
        </Grid>
        <Grid item xs={12} md={3}>
          <KpiCard label="Nudge Follow-through Rate" value={percent(metrics.action_rate_7d)} subtext="Attributed habit movement / nudges, 7d" tone={metrics.action_rate_7d > 30 ? "positive" : "warning"} />
        </Grid>
        <Grid item xs={12} md={3}>
          <KpiCard label="Dismiss Rate" value={percent(metrics.dismiss_rate)} subtext="Later / dismissed responses" tone={metrics.dismiss_rate > 25 ? "warning" : "neutral"} />
        </Grid>
        <Grid item xs={12} md={6}>
          <ChartCard title="Nudge Response Funnel">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={funnelRows} margin={{ top: 24, right: 18, left: -12, bottom: 4 }}>
                <CartesianGrid vertical={false} stroke="#e2e8f0" />
                <XAxis dataKey="step" tickLine={false} axisLine={false} />
                <YAxis tickLine={false} axisLine={false} />
                <Tooltip />
                <Bar dataKey="count" fill={colors.green} radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <ChartCard title="Outcome by Nudge Family">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={familyRows} margin={{ top: 24, right: 18, left: -12, bottom: 4 }}>
                <CartesianGrid vertical={false} stroke="#e2e8f0" />
                <XAxis dataKey="family" tickLine={false} axisLine={false} />
                <YAxis tickLine={false} axisLine={false} />
                <Tooltip />
                <Legend />
                <Bar dataKey="sent" fill={colors.blue} radius={[8, 8, 0, 0]} />
                <Bar dataKey="acted" fill={colors.green} radius={[8, 8, 0, 0]} />
                <Bar dataKey="dismissed" fill={colors.yellow} radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
        <Grid item xs={12}>
          <ChartCard title="Nudges by Type">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={rows} margin={{ top: 24, right: 18, left: -12, bottom: 4 }}>
                <CartesianGrid vertical={false} stroke="#e2e8f0" />
                <XAxis dataKey="type" tickLine={false} axisLine={false} />
                <YAxis tickLine={false} axisLine={false} />
                <Tooltip />
                <Bar dataKey="sent" fill={colors.blue} radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
      </Grid>
    </Section>
  );
}

export default function AdminAnalyticsPage() {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [activeTab, setActiveTab] = React.useState("command");

  React.useEffect(() => {
    let mounted = true;
    async function load() {
      setLoading(true);
      setError("");
      try {
        const [overview, funnel, sprints, drift, social, notifications] = await Promise.all(
          Object.values(endpoints).map((endpoint) => apiFetch(endpoint))
        );
        if (mounted) setData({ overview, funnel, sprints, drift, social, notifications });
      } catch (err) {
        if (mounted) setError(err?.message || "Unable to load analytics.");
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => {
      mounted = false;
    };
  }, []);

  if (loading) {
    return (
      <LoadingState
        title="Loading analytics"
        body="Aggregating behavior, consistency, and accountability signals."
        rows={5}
      />
    );
  }

  if (error) return <Alert severity="error">{error}</Alert>;

  const overview = data?.overview?.metrics || {};
  const funnelRows = normalizeFunnel(data?.funnel?.steps || []);
  const sprintMetrics = data?.sprints?.metrics || {};
  const socialMetrics = data?.social?.metrics || {};
  const driftMetrics = data?.drift?.metrics || {};
  const insights = buildInsights({
    funnelRows,
    sprintRows: data?.sprints?.day_breakdown || [],
    driftMetrics,
  });

  function renderActiveTab() {
    if (activeTab === "overview") {
      return (
        <OverviewTab
          overview={overview}
          retention={data?.overview?.retention}
          sprintMetrics={sprintMetrics}
          socialMetrics={socialMetrics}
          users={data?.overview?.users}
        />
      );
    }
    if (activeTab === "funnel") {
      return (
        <>
          <EndpointNote endpointKey="funnel" />
          <FunnelChart rows={funnelRows} />
          <PrimaryLeakCard rows={funnelRows} />
          <FunnelDropoffDrilldown dropoffs={data?.funnel?.dropoffs || []} />
        </>
      );
    }
    if (activeTab === "sprints") {
      return (
        <SprintMetricsTab
          overview={overview}
          sprintMetrics={sprintMetrics}
          socialMetrics={socialMetrics}
          sprints={data?.sprints}
          drift={data?.drift}
        />
      );
    }
    if (activeTab === "drift") {
      return (
        <>
          <EndpointNote endpointKey="drift" />
          <ConsistencyCharts sprints={data?.sprints} drift={data?.drift} />
          <RecoverySection drift={data?.drift} />
          <UserDrilldown
            groups={[
              { title: "At-risk users", users: data?.drift?.users?.at_risk },
              { title: "Recovered users", users: data?.drift?.users?.recovered },
            ]}
          />
        </>
      );
    }
    if (activeTab === "social") {
      return (
        <>
          <EndpointNote endpointKey="social" />
          <AccountabilityComparison social={data?.social} />
          <UserDrilldown
            groups={[
              { title: "With TrustBud", users: data?.social?.users?.with_partner },
              { title: "Without TrustBud", users: data?.social?.users?.without_partner },
            ]}
          />
          <ConnectionDrilldown connections={data?.social?.users?.active_connections || []} />
        </>
      );
    }
    if (activeTab === "notifications") {
      return (
        <>
          <EndpointNote endpointKey="notifications" />
          <NudgeEffectiveness notifications={data?.notifications} />
          <NudgeDrilldown nudges={data?.notifications?.users?.recent_nudges || []} />
          <UserDrilldown
            title="Nudge Follow-through Users"
            groups={[
              { title: "Acted after nudge", users: data?.notifications?.users?.acted },
            ]}
          />
        </>
      );
    }
    if (activeTab === "raw") {
      return <RawApiExplorer data={data} />;
    }
    return (
      <>
        <InsightCards insights={insights} />
        <KpiRow overview={overview} sprintMetrics={sprintMetrics} socialMetrics={socialMetrics} />
        <FunnelChart rows={funnelRows} />
        <PrimaryLeakCard rows={funnelRows} />
        <ConsistencyCharts sprints={data?.sprints} drift={data?.drift} />
        <RecoverySection drift={data?.drift} />
        <UserDrilldown
          title="Users To Inspect"
          groups={[
            { title: "At-risk users", users: data?.drift?.users?.at_risk },
            { title: "Inactive 7d", users: data?.overview?.users?.inactive_7d },
            { title: "Needs reflection", users: (data?.sprints?.users?.needs_reflection || []).flatMap((sprint) => [sprint.user_a, sprint.user_b]) },
          ]}
        />
        <AccountabilityComparison social={data?.social} />
        <NudgeEffectiveness notifications={data?.notifications} />
      </>
    );
  }

  return (
    <Box
      sx={{
        maxWidth: 1320,
        mx: "auto",
        px: { xs: 1, md: 2 },
        pb: 6,
      }}
    >
      <PageHeader />
      <AnalyticsTabs value={activeTab} onChange={setActiveTab} />
      {renderActiveTab()}
    </Box>
  );
}
